export type ConnectorFamily = 'REST' | 'SOAP' | 'JDBC' | 'AD/LDAP' | 'Identity' | 'Unknown'

export type LifecyclePhaseId =
  | 'discover'
  | 'credentials'
  | 'generate'
  | 'validate'
  | 'approve'
  | 'deploy'

export type LifecyclePhaseState = 'not_started' | 'active' | 'blocked' | 'complete' | 'failed'

export interface WorkspaceProjectState {
  name?: string
  target_system?: string
  connection_name?: string
  connection_type?: string
  saviynt_url?: string
  saviynt_username?: string
  secret_count?: number
  has_saviynt_username_secret?: boolean
  has_saviynt_password_secret?: boolean
  deployment_mode?: string
}

export interface LifecyclePhase {
  id: LifecyclePhaseId
  label: string
  state: LifecyclePhaseState
  reason?: string
  nextAction?: string
}

export interface WorkspaceState {
  connectorFamily: ConnectorFamily
  status: string
  statusLabel: string
  statusSource: 'explicit' | 'inferred'
  activePhase: LifecyclePhaseId
  phases: LifecyclePhase[]
  nextAction: string
}

export interface WorkspaceLifecycleEvidence {
  hasArtifactVersion: boolean
  artifactState?: string | null
  validationStatus?: string | null
  latestReviewAction?: string | null
}

function includesAny(value: string, terms: string[]) {
  return terms.some(term => value.includes(term))
}

export function inferWorkspaceConnectorFamily(project?: WorkspaceProjectState | null): ConnectorFamily {
  const explicit = String(project?.connection_type || '').toUpperCase()
  if (['REST', 'SOAP', 'JDBC', 'AD/LDAP'].includes(explicit)) return explicit as ConnectorFamily
  const source = `${project?.connection_name || ''}`.toLowerCase()
  if (includesAny(source, ['schemauserjob', 'schema user', 'identity import', 'feed file'])) return 'Identity'
  if (includesAny(source, ['soap', 'wsdl'])) return 'SOAP'
  if (includesAny(source, ['jdbc', 'sql server', 'oracle', 'postgres', 'mysql', 'database'])) return 'JDBC'
  if (includesAny(source, ['active directory', 'ad connector', 'ldap', 'ldaps'])) return 'AD/LDAP'
  if (includesAny(source, ['rest', 'api', 'oauth', 'servicenow', 'workday'])) return 'REST'
  return 'Unknown'
}

function phase(
  id: LifecyclePhaseId,
  label: string,
  state: LifecyclePhaseState,
  reason?: string,
  nextAction?: string,
): LifecyclePhase {
  return { id, label, state, reason, nextAction }
}

export function deriveWorkspaceState(
  project: WorkspaceProjectState | null | undefined,
  rawStatus?: string | null,
  evidence?: WorkspaceLifecycleEvidence | null,
): WorkspaceState {
  const status = (rawStatus || 'draft').toLowerCase()
  const artifactState = (evidence?.artifactState || '').toLowerCase()
  const validationStatus = (evidence?.validationStatus || '').toLowerCase()
  const latestReviewAction = (evidence?.latestReviewAction || '').toLowerCase()
  const connectorFamily = inferWorkspaceConnectorFamily(project)
  // Conversations are connection-scoped. The legacy project target is useful as
  // display metadata only and must never gate the active connection lifecycle.
  const hasScope = Boolean(project?.connection_name)
  const hasSaviyntAccess = Boolean(
    project?.saviynt_url
    && (project?.saviynt_username || project?.has_saviynt_username_secret)
    && project?.has_saviynt_password_secret
  )
  const generated = Boolean(evidence?.hasArtifactVersion)
    || ['generated', 'saved', 'tested', 'validated', 'approved', 'deployed'].includes(status)
  const validated = ['passed', 'pass', 'validated'].includes(validationStatus)
    || ['tested', 'validated', 'approved', 'deployed'].includes(status)
  const approved = ['approved', 'deployed'].includes(latestReviewAction)
    || ['approved', 'deployed'].includes(artifactState)
    || ['approved', 'deployed'].includes(status)
  const deployed = latestReviewAction === 'deployed' || artifactState === 'deployed' || status === 'deployed'
  const failed = status === 'failed' || artifactState === 'failed'
    || ['failed', 'error'].includes(validationStatus)
  const scopeComplete = hasScope || generated
  const credentialsComplete = hasSaviyntAccess || generated

  const phases: LifecyclePhase[] = [
    phase(
      'discover',
      'Discover',
      scopeComplete ? 'complete' : 'active',
      hasScope ? 'The active conversation is assigned to a connection.' : generated ? 'Recorded connector state confirms discovery completed.' : 'Select or create a connection for this conversation.',
      scopeComplete ? undefined : 'Select connection',
    ),
    phase(
      'credentials',
      'Credentials',
      credentialsComplete ? 'complete' : scopeComplete ? 'blocked' : 'not_started',
      hasSaviyntAccess ? 'Saviynt access details are configured.' : generated ? 'Recorded connector state confirms credentialed work completed.' : 'Saviynt URL, username, or secret is missing.',
      credentialsComplete ? undefined : 'Open project settings',
    ),
    phase(
      'generate',
      'Generate',
      generated ? 'complete' : failed ? 'failed' : credentialsComplete ? 'active' : 'not_started',
      generated ? 'A configuration state has been recorded.' : failed ? 'The latest configuration action failed.' : 'Generate a connector configuration in chat.',
      generated ? undefined : failed ? 'Review failure' : 'Generate configuration',
    ),
    phase(
      'validate',
      'Validate',
      validated ? 'complete' : failed ? 'failed' : generated ? 'active' : 'not_started',
      validated ? 'The current configuration has been tested.' : failed ? 'The latest validation or deployment action failed.' : 'Validation has not completed.',
      validated ? undefined : generated ? 'Run validation' : undefined,
    ),
    phase(
      'approve',
      'Approve',
      approved ? 'complete' : validated ? 'active' : 'not_started',
      approved ? 'The exact current version is approved.' : validated ? 'The exact validated version is ready for review.' : 'Approval requires a validated configuration version.',
      approved ? undefined : validated ? 'Review validated configuration' : undefined,
    ),
    phase(
      'deploy',
      'Deploy',
      deployed ? 'complete' : failed && validated ? 'failed' : approved ? 'active' : 'not_started',
      deployed ? 'The current configuration is deployed.' : 'Deployment has not completed.',
      deployed ? undefined : approved ? 'Deploy approved configuration' : undefined,
    ),
  ]

  const current = phases.find(item => ['active', 'blocked', 'failed'].includes(item.state)) || phases[phases.length - 1]
  const statusLabels: Record<string, string> = {
    draft: 'Draft', generated: 'Generated', saved: 'Saved', tested: 'Validated', validated: 'Validated',
    approved: 'Approved', deployed: 'Deployed', failed: 'Failed',
  }

  return {
    connectorFamily,
    status,
    statusLabel: statusLabels[status] || status.replace(/_/g, ' '),
    statusSource: rawStatus ? 'explicit' : 'inferred',
    activePhase: current.id,
    phases,
    nextAction: current.nextAction || current.reason || 'Continue in chat',
  }
}

export interface FileArtifactPresentation {
  schemaFileName: string
  schemaContent: string
  sourceFileName: string
  delimiter: string
  sourceHeaders: string[]
  savHeaders: string[]
  mappings: Array<{ position: number; source: string; target: string }>
}

export function fileArtifactPresentation(config: Record<string, unknown>): FileArtifactPresentation {
  const schemaContent = String(config.schema_content || config.schemaContent || '')
  const lines = schemaContent.split(/\r?\n/).map(line => line.trim()).filter(Boolean)
  const directive = (name: string) => {
    const prefix = `#${name.toUpperCase()}=`
    return lines.find(line => line.toUpperCase().startsWith(prefix))?.slice(prefix.length).trim() || ''
  }
  const sourceHeaders = Array.isArray(config.source_headers)
    ? config.source_headers.map(value => String(value).trim()).filter(Boolean)
    : []
  const savHeaderLine = lines.find(line => !line.startsWith('#')) || ''
  const savHeaders = savHeaderLine.split(',').map(value => value.trim()).filter(Boolean)
  const width = Math.max(sourceHeaders.length, savHeaders.length)

  return {
    schemaFileName: String(config.schema_file_name || 'schema_users.sav'),
    schemaContent,
    sourceFileName: String(config.source_file_name || directive('FILENAME') || ''),
    delimiter: String(config.delimiter || directive('DELIMITER') || ','),
    sourceHeaders,
    savHeaders,
    mappings: Array.from({ length: width }, (_, index) => ({
      position: index + 1,
      source: sourceHeaders[index] || '—',
      target: savHeaders[index] || '—',
    })),
  }
}

export function secretBindingLabel(binding: { secret_names: string[]; mechanism: string }) {
  if (binding.mechanism === 'unnamed' || binding.secret_names.length === 0) return 'No named secret'
  const names = binding.secret_names.join(' + ')
  if (binding.mechanism === 'base64') return `${names} → Base64`
  if (binding.mechanism === 'external') return `${names} · external reference`
  return names
}
